import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import React from "react";
import { useHistory } from "react-router-dom";
import { InvestmentTypeEnumType } from "../../../api/enums";
import { TrackRecordModel } from "../../../api/models";
import { ChevronLeftIcon, ChevronRightIcon } from "../../../icons";
import { formatMoney } from "../../../utils";
import { dateToString } from "../../../utils/dates";
import { getOpportunityBaseUrl } from "../../../utils/opportunities";
import { getSortByObjectValue } from "../../../utils/sorting";
import { IconButton } from "../../common/IconButton";
import {
  Container,
  Table as TableComponent,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "../../common/Table";
import { TableTrProps } from "../../common/Table/Table.types";
import { VerifiedIcon } from "../../common/VerifiedIcon";
import { TrackRecordNameColumnRender } from "./TrackRecordNameColumnRender";
import { TrackRecordNoResults } from "./TrackRecordNoResults";

const Table = {
  Container,
  Table: TableComponent,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
};

export interface TrackRecordListProps {
  trackRecord: TrackRecordModel[];
  status: string;
  selectedInvestmentType: InvestmentTypeEnumType;
  selectedOpportunity: string;
  isEditing: boolean;
  clearAllFilters: () => void;
}

export const TrackRecordList: React.FC<TrackRecordListProps> = ({
  trackRecord,
  status,
  selectedInvestmentType,
  selectedOpportunity,
  isEditing,
  clearAllFilters,
}) => {
  const RESULTS_PER_PAGE = 10;
  const [sortable, setSortable] = React.useState<{
    sort: "asc" | "desc";
    type: string;
    name: string;
  }>({ sort: "asc", type: "", name: "" });
  let data = [...trackRecord];
  if (status) {
    data = data.filter(
      (d) =>
        (status === "active" && !d.realizationDate) ||
        (status === "realized" && d.realizationDate) ||
        status === "all" ||
        !status,
    );
  }

  if (selectedInvestmentType) {
    data = data.filter((d) => d.investmentType === selectedInvestmentType);
  }

  if (selectedOpportunity) {
    data = data.filter((d) => d.opportunityTag === selectedOpportunity);
  }

  if (sortable && sortable.name) {
    data = data.sort(
      getSortByObjectValue(sortable.name, sortable.sort || "asc"),
    );
  }

  const maxPage = Math.ceil(data.length / RESULTS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState(1);

  const sort = (name?: string, type?: string) => () => {
    if (sortable && sortable.name === name) {
      if (sortable.sort === "asc") {
        setSortable({ ...sortable, sort: "desc" });
      } else {
        setSortable({ ...sortable, sort: "asc" });
      }
    } else {
      setSortable({ name: name || "", type: type || "", sort: "asc" });
    }
  };

  const isSorted = (name: string) => {
    if (sortable && sortable.name === name) {
      return sortable.sort;
    }

    return false;
  };
  const history = useHistory();
  React.useEffect(() => {
    setCurrentPage(1);
  }, [status, selectedInvestmentType, selectedOpportunity]);

  if (
    !data.length &&
    (selectedInvestmentType || selectedOpportunity || status)
  ) {
    return <TrackRecordNoResults clearFilters={clearAllFilters} />;
  }

  const tableBadgeMessages = {
    verified: "Verivest verified",
    notVerified: "Unverified",
  };

  return (
    <Box
      borderRadius={4}
      boxShadow={1}
      border={"1px solid"}
      borderColor={"border.grey"}
      backgroundColor={isEditing ? "bg.white" : "transparent"}
      p={6}
    >
      {/* <Table.Container bg="white" p={4} display={{ base: "none", md: "block" }}> */}
      <Table.Table
        className="track-record-table"
        overflowX={{ base: "auto", md: "unset" }}
        display={{ base: "block", md: "table" }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th
              colSpan={2}
              sortable
              sort={isSorted("investmentName") as never}
              onClick={sort("investmentName")}
            >
              Investment
            </Table.Th>
            <Table.Th
              title="Inception Date"
              align="right"
              sortable
              sort={isSorted("inceptionDate") as never}
              onClick={sort("inceptionDate", "date")}
            >
              Inception Date
            </Table.Th>
            <Table.Th
              title="Investment Amount"
              align="right"
              sortable
              sort={isSorted("totalCost") as never}
              onClick={sort("totalCost")}
            >
              Investment Amount
            </Table.Th>
            <Table.Th
              title="Realization Date"
              align="right"
              sortable
              sort={isSorted("realizationDate") as never}
              onClick={sort("realizationDate", "date")}
            >
              Realization Date
            </Table.Th>
            <Table.Th
              title="Total Proceeds"
              align="right"
              sortable
              sort={isSorted("totalProceeds") as never}
              onClick={sort("totalProceeds")}
            >
              Total Proceeds
            </Table.Th>
            <Table.Th
              title="Realized IRR"
              align="right"
              sortable
              sort={isSorted("realizedIrr") as never}
              onClick={sort("realizedIrr")}
            >
              Realized IRR
            </Table.Th>
            <Table.Th
              title="Realized Equity Multiple"
              align="right"
              sortable
              sort={isSorted("realizedEquityMultiple") as never}
              onClick={sort("realizedEquityMultiple")}
            >
              Realized Equity Multiple
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data
            .slice(
              (currentPage - 1) * RESULTS_PER_PAGE,
              currentPage * RESULTS_PER_PAGE,
            )
            .map((trItem) => {
              // TODO - should fix the model export discrepancy between API and Placements
              const trackRecordItem = trItem as typeof trItem & {
                sponsorSlug?: string;
                opportunityId?: number;
                opportunitySlug?: string;
              };
              let trProps: TableTrProps = {
                _hover: { bg: "bg.greyLighter", cursor: "pointer" },
                onClick: () =>
                  history.push(
                    `${getOpportunityBaseUrl(
                      trackRecordItem.sponsorSlug || trackRecordItem.managerId,
                    )}
                      /${
                        trackRecordItem.opportunitySlug ||
                        trackRecordItem.opportunityId
                      }`,
                  ),
              };
              if (
                !trackRecordItem.opportunityId ||
                trackRecordItem.opportunityId === 0
              ) {
                trProps = {};
              }
              return (
                <Table.Tr {...trProps} key={trackRecordItem.id}>
                  <Table.Td colSpan={2}>
                    <TrackRecordNameColumnRender
                      record={trackRecordItem}
                      isView
                    />
                  </Table.Td>
                  <Table.Td>
                    {!trackRecordItem.inceptionDate ? (
                      <Flex float="right">--</Flex>
                    ) : (
                      <Flex float="right">
                        {trackRecordItem.inceptionDate
                          ? dateToString(
                              new Date(trackRecordItem.inceptionDate),
                            )
                          : "-"}
                        <VerifiedIcon
                          notVerified={!trackRecordItem.verifiedInceptionDate}
                          iconProps={{ boxSize: "16px", ml: "4px" }}
                          messages={tableBadgeMessages}
                        />
                      </Flex>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {!trackRecordItem.totalCost ? (
                      <Flex float="right">--</Flex>
                    ) : (
                      <Flex float="right">
                        {"$" + formatMoney(trackRecordItem.totalCost, 0)}
                        <VerifiedIcon
                          iconProps={{ boxSize: "16px", ml: "4px" }}
                          notVerified={!trackRecordItem.verifiedTotalCost}
                          messages={tableBadgeMessages}
                        />
                      </Flex>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {!trackRecordItem.realizationDate ? (
                      <Flex float="right">--</Flex>
                    ) : (
                      <Flex float="right">
                        {trackRecordItem.realizationDate
                          ? dateToString(
                              new Date(trackRecordItem.realizationDate),
                            )
                          : "-"}
                        <VerifiedIcon
                          iconProps={{ boxSize: "16px", ml: "4px" }}
                          notVerified={!trackRecordItem.verifiedRealizationDate}
                          messages={tableBadgeMessages}
                        />
                      </Flex>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {!trackRecordItem.totalProceeds ? (
                      <Flex float="right">--</Flex>
                    ) : (
                      <Flex float="right">
                        {"$" + formatMoney(trackRecordItem.totalProceeds, 0)}
                        <VerifiedIcon
                          iconProps={{
                            boxSize: "16px",
                            ml: "4px",
                          }}
                          notVerified={!trackRecordItem.verifiedTotalProceeds}
                          messages={tableBadgeMessages}
                        />
                      </Flex>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {!trackRecordItem.realizedIrr ? (
                      <Flex float="right">--</Flex>
                    ) : (
                      <Flex float="right">
                        {(trackRecordItem.realizedIrr || "--") + "%"}
                        <VerifiedIcon
                          iconProps={{ boxSize: "16px", ml: "4px" }}
                          notVerified={!trackRecordItem.verifiedRealizedIrr}
                          messages={tableBadgeMessages}
                        />
                      </Flex>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {!trackRecordItem.realizedEquityMultiple ? (
                      <Flex float="right">
                        <span>--</span>
                      </Flex>
                    ) : (
                      <Flex float="right">
                        {(trackRecordItem.realizedEquityMultiple || "--") + "x"}
                        <VerifiedIcon
                          iconProps={{ boxSize: "16px", ml: "4px" }}
                          notVerified={
                            !trackRecordItem.verifiedRealizedEquityMultiple
                          }
                          messages={tableBadgeMessages}
                        />
                      </Flex>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
        </Table.Tbody>
      </Table.Table>

      {maxPage > 1 && (
        <Flex alignItems="center" justifyContent="center" mt="24px">
          <IconButton
            p="4px"
            isDisabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            <Icon as={ChevronLeftIcon} boxSize="24px" color="icons.grey" />
          </IconButton>
          <Text textStyle="bodySmall" mx="16px">
            Page {currentPage} of {maxPage}
          </Text>
          <IconButton
            p="4px"
            isDisabled={currentPage === maxPage}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            <Icon as={ChevronRightIcon} boxSize="24px" color="icons.grey" />
          </IconButton>
        </Flex>
      )}
      {/* </Table.Container> */}
    </Box>
  );
};
